import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import {
  ConfigurationKey,
  configurations,
  ConfigurationCategory,
  ConfigurationValueType,
} from '../../../../../src/configuration/dto/configuration.dto';
import {
  FormControl,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from "@angular/forms";
import { UpdateConfigurationDto } from '../../../../../src/configuration/dto/update-configuration.dto';
import { ConfigurationService } from '../configuration.service';
import { InpostCategoryService } from '../inpost-category.service';
import { SnackService } from '../../common/snack.service';
import { take } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { InpostCategoryDialogComponent } from '../inpost-category-dialog/inpost-category-dialog.component';

@Component({
  selector: 'app-configuration-form',
  templateUrl: './configuration-form.component.html',
  styleUrls: ['./configuration-form.component.scss'],
})
export class ConfigurationFormComponent implements OnInit {
  public configurations: Map<ConfigurationKey, {valueType: ConfigurationValueType, restricted: boolean, comboBoxOptions?: string[], category: ConfigurationCategory}> = configurations;
  public form: UntypedFormGroup;
  public ConfigurationValueType = ConfigurationValueType;
  public configsLoaded: boolean = false;
  public categories: ConfigurationCategory[] = [];
  public categoryConfigIndices: Map<ConfigurationCategory, number[]> = new Map();
  public categoriesRefreshing = false;
  public categoriesResetting = false;
  public categoriesReassigning = false;
  public categoriesRefreshResult = '';

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly configurationService: ConfigurationService,
    private readonly inpostCategoryService: InpostCategoryService,
    private readonly cd: ChangeDetectorRef,
    private readonly snackService: SnackService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      configs: this.formBuilder.array([]),
    });
    this.loadAndapplyConfigs();
  }

  configs() {
    return this.form.get('configs') as UntypedFormArray;
  }

  private loadAndapplyConfigs() {
    this.configurationService.getConfigs().pipe(take(1)).subscribe(
      (configs: UpdateConfigurationDto[]) => {
        const categoryOrder: ConfigurationCategory[] = ['General', 'Allegro', 'InterCars', 'InPost'];
        const categoryIndicesMap = new Map<ConfigurationCategory, number[]>();

        configs.forEach((config, index) => {
          this.configs().push(this.newConfigFormGroup(config));
          const meta = this.configurations.get(config.key);
          const category = meta?.category || 'General';
          if (!categoryIndicesMap.has(category)) {
            categoryIndicesMap.set(category, []);
          }
          categoryIndicesMap.get(category).push(index);
        });

        this.categories = categoryOrder.filter(c => categoryIndicesMap.has(c));
        this.categoryConfigIndices = categoryIndicesMap;
        this.configsLoaded = true;
        this.cd.detectChanges();
      },
    );
  }

  getConfigIndicesForCategory(category: ConfigurationCategory): number[] {
    return this.categoryConfigIndices.get(category) || [];
  }

  newConfigFormGroup(config: UpdateConfigurationDto = null): UntypedFormGroup {
    const meta = this.configurations.get(config.key);
    // For boolean configs, ensure the value is a proper boolean (not 0, null, "false", etc.)
    let configValue = config.value;
    if (meta?.valueType === ConfigurationValueType.BOOLEAN) {
      configValue = configValue === true || configValue === 'true' || (configValue as any) === 1;
    }
    return this.formBuilder.group({
      key: new UntypedFormControl(config.key),
      value: new UntypedFormControl({value: configValue, disabled: meta?.restricted}),
    });
  }

  openCategoryDialog(configIndex: number): void {
    const dialogRef = this.dialog.open(InpostCategoryDialogComponent, {
      width: '600px',
      maxHeight: '80vh',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const control = this.configs().at(configIndex);
        control.get('value').setValue(result.id);
        control.markAsDirty();
      }
    });
  }

  refreshCategories(): void {
    this.categoriesRefreshing = true;
    this.categoriesRefreshResult = '';
    this.inpostCategoryService.refreshCategories().pipe(take(1)).subscribe(
      (result) => {
        this.categoriesRefreshing = false;
        this.categoriesRefreshResult = `Pobrano ${result.leafCategories} kategorii liściowych`;
        this.cd.detectChanges();
      },
      (error) => {
        this.categoriesRefreshing = false;
        this.categoriesRefreshResult = 'Błąd odświeżania kategorii';
        this.cd.detectChanges();
      },
    );
  }

  resetCategoryCache(): void {
    this.categoriesResetting = true;
    this.categoriesRefreshResult = '';
    this.inpostCategoryService.resetCategoryCache().pipe(take(1)).subscribe(
      (result) => {
        this.categoriesResetting = false;
        this.categoriesRefreshResult = `Zresetowano cache dla ${result.resetCount} produktów`;
        this.cd.detectChanges();
      },
      (error) => {
        this.categoriesResetting = false;
        this.categoriesRefreshResult = 'Błąd resetowania cache';
        this.cd.detectChanges();
      },
    );
  }

  reassignCategories(): void {
    this.categoriesReassigning = true;
    this.categoriesRefreshResult = '';
    this.inpostCategoryService.reassignCategories().pipe(take(1)).subscribe(
      (result) => {
        this.categoriesReassigning = false;
        if (result) {
          this.categoriesRefreshResult = `Zsync.: ${result.synced}, Naprawione: ${result.fixed}, Brak ref.: ${result.noRef}, Bledy: ${result.failed}`;
        } else {
          this.categoriesRefreshResult = 'Brak odpowiedzi';
        }
        this.cd.detectChanges();
      },
      (error) => {
        this.categoriesReassigning = false;
        this.categoriesRefreshResult = 'Blad reassign kategorii';
        this.cd.detectChanges();
      },
    );
  }

submit($event: SubmitEvent) {
    if (this.form.valid) {
      const dirtyConfigs = this.configs()
        .controls.filter(ctrl => ctrl.dirty)
        .map(ctrl => {
          const val = ctrl.value;
          return val;
        });
      this.configurationService
        .updateConfigs(dirtyConfigs)
        .then(() => this.snackService.openSuccessSnackBar('Konfiguracja zapisana poprawnie', 'OK'))
        .catch(() =>
          this.snackService.openErrorSnackBar(
            'Nie udało się zapisać konfiguracji, spróbuj ponownie',
            'OK',
          ),
        );
    } else {
      this.snackService.openErrorSnackBar('Podano nieprawidłowe wartości, proszę poprawić', 'OK');
    }
  }
}
